import { Separator as BaseSeparator } from "@base-ui-components/react/separator";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Base UI Separator — the single implementation behind both import paths.
 * `ui/primitives` is a bare `export { Separator } from "./separator"`, so
 * there is exactly ONE look: the `--border` token hardcoded below. The
 * obsidian-only `--gv-line` wrapper that once specialized it collapsed onto
 * `--border` in the Wave 2b token sweep. Reconciles what used to be two
 * independent `@radix-ui/react-separator` instances.
 *
 * Base UI has no Radix-style `decorative` flag baked in — it always renders
 * `role="separator"` + `aria-orientation`. `decorative` (default `true`,
 * matching the old shadcn/Radix default) reproduces the old behavior by
 * overriding the role to `"none"` so screen readers skip a purely visual
 * rule.
 */
const Separator = React.forwardRef<
  React.ElementRef<typeof BaseSeparator>,
  React.ComponentPropsWithoutRef<typeof BaseSeparator> & { decorative?: boolean }
>(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => (
  <BaseSeparator
    ref={ref}
    orientation={orientation}
    role={decorative ? "none" : undefined}
    className={cn(
      "shrink-0 bg-border",
      orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
      className,
    )}
    {...props}
  />
));
Separator.displayName = "Separator";

export { Separator };
